import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Info } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { OpsiumPolicy, POLICY_DEPENDENCIES } from '@/modules/common/types/opsium-policy';

import { UserGroupColumnHeader } from '../components/permissions/user-group-column-header';
import {
  groupFieldPath,
  PermissionRow,
  toUserGroupFieldKey,
  UserGroupPermissions,
} from '../types/permission';
import { getGroupMenuDisabledReasons, isSuperAdminUserGroup } from '../utils/user-group.utils';
import { PermissionsFormDataValidated } from '../validations/permission-validation';

interface GetColumnsParams {
  /** User groups in API order — one column each */
  userGroups: UserGroupPermissions[];
  policyIndexMap: Partial<Record<OpsiumPolicy, number>>;
  onRenameGroup: (group: UserGroupPermissions) => void;
  onDuplicateGroup: (group: UserGroupPermissions) => void;
  onDeleteGroup: (group: UserGroupPermissions) => void;
  onSetDefaultGroup: (group: UserGroupPermissions) => void;
  t: TFunction;
}

/** Column id of the pinned permission-name column */
export const PERMISSION_POLICY_COLUMN_ID = 'policy';

/**
 * Maps OpsiumPolicy enum values to their corresponding tooltip translation keys.
 * Translation keys are independent from policy names to prevent breaking changes.
 * Only policies with explanatory tooltips are included in this mapping.
 */
const POLICY_TOOLTIP_KEYS: Partial<Record<OpsiumPolicy, string>> = {
  [OpsiumPolicy.PlannerFinancialsView]: 'plannerFinancialsView',
  [OpsiumPolicy.AccessGrantAndRevoke]: 'grantRevokeAccess',
  [OpsiumPolicy.ExternalRatesManage]: 'externalRatesManage',
  [OpsiumPolicy.InternalRatesManage]: 'internalRatesManage',
  [OpsiumPolicy.OrgStructureViewAndEdit]: 'editOrgStructure',
  [OpsiumPolicy.OrgSettingsManage]: 'manageOrgSettings',
  [OpsiumPolicy.AccessAndPermissionsManage]: 'manageAccessPermissions',
};

interface PermissionCheckboxCellProps {
  rowIndex: number;
  policy: OpsiumPolicy;
  /** `g${groupId}` key of the user group column this cell belongs to */
  groupKey: string;
  policyIndexMap: Partial<Record<OpsiumPolicy, number>>;
  t: TFunction;
  /** When true the checkbox is always disabled regardless of dependency state (protected group) */
  alwaysDisabled?: boolean;
}

/**
 * Renders a single permission checkbox cell.
 * Watches only its dependency field so re-renders are minimal.
 * Disabled when the required dependency policy is unchecked for the same user group.
 */
function PermissionCheckboxCell({
  rowIndex,
  policy,
  groupKey,
  policyIndexMap,
  t,
  alwaysDisabled,
}: PermissionCheckboxCellProps) {
  const { control } = useFormContext<PermissionsFormDataValidated>();

  const requiredPolicy = POLICY_DEPENDENCIES[policy];
  const requiredPolicyIndex =
    requiredPolicy !== undefined ? policyIndexMap[requiredPolicy] : undefined;

  // Watch only the specific dependency field to avoid table-wide re-renders.
  // Falls back to a stable no-op field when there is no dependency.
  const dependencyValue = useWatch({
    control,
    name:
      requiredPolicyIndex !== undefined
        ? groupFieldPath(requiredPolicyIndex, groupKey)
        : (`permissions.0.policy` as const),
  });

  const isDisabledDueToDependency = requiredPolicyIndex !== undefined && !dependencyValue;

  const disabledTooltipText =
    isDisabledDueToDependency && requiredPolicy
      ? t('pages.permissions.tooltips.disabledDependency', {
          policyName: t(`pages.permissions.policies.${requiredPolicy}`),
        })
      : undefined;

  const checkbox = (
    <FormField
      control={control}
      name={groupFieldPath(rowIndex, groupKey)}
      render={({ field }) => (
        <FormItem className="flex items-center justify-center">
          <FormControl>
            <Checkbox
              checked={field.value as boolean}
              onCheckedChange={field.onChange}
              disabled={alwaysDisabled || isDisabledDueToDependency}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );

  if (disabledTooltipText) {
    return (
      <SimpleTooltip
        content={<p className="leading-relaxed">{disabledTooltipText}</p>}
        contentClassName="max-w-xs"
      >
        {/* span needed: disabled elements don't fire pointer events */}
        <span className="flex cursor-not-allowed items-center justify-center">{checkbox}</span>
      </SimpleTooltip>
    );
  }

  return checkbox;
}

export const getPermissionsColumns = ({
  userGroups,
  policyIndexMap,
  onRenameGroup,
  onDuplicateGroup,
  onDeleteGroup,
  onSetDefaultGroup,
  t,
}: GetColumnsParams): ColumnDef<PermissionRow>[] => {
  const policyColumn: ColumnDef<PermissionRow> = {
    id: PERMISSION_POLICY_COLUMN_ID,
    accessorKey: 'policy',
    size: 320,
    minSize: 240,
    enablePinning: true,
    header: () => {
      return (
        <div className="font-semibold" style={{ minWidth: '10rem' }}>
          {t('pages.permissions.columns.permission')}
        </div>
      );
    },
    cell: ({ row }) => {
      const policy = row.original.policy as OpsiumPolicy;
      const policyText = t(`pages.permissions.policies.${policy}`);
      const tooltipKey = POLICY_TOOLTIP_KEYS[policy];

      if (!tooltipKey) {
        return <div className="text-sm text-foreground">{policyText}</div>;
      }

      const tooltipText = t(`pages.permissions.tooltips.${tooltipKey}`);

      return (
        <SimpleTooltip
          content={<p className="leading-relaxed">{tooltipText}</p>}
          contentClassName="max-w-xs"
        >
          <div className="flex cursor-help items-center gap-1.5 text-sm text-foreground">
            <span>{policyText}</span>
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </SimpleTooltip>
      );
    },
  };

  // No accessorKey: cell values live in the form state, not in the row data
  const groupColumns: ColumnDef<PermissionRow>[] = userGroups.map(group => {
    const groupKey = toUserGroupFieldKey(group.id);
    // Super Admin only: its policies are rejected by the backend, so its cells stay locked.
    const isSuperAdmin = isSuperAdminUserGroup(group);
    const disabledReasons = getGroupMenuDisabledReasons({
      group,
      allGroups: userGroups,
      translate: key => t(key),
    });

    return {
      id: `group-${group.id}`,
      size: 120,
      minSize: 120,
      meta: {
        cellClassName: 'p-0',
      },
      header: () => (
        <UserGroupColumnHeader
          group={group}
          onRename={onRenameGroup}
          onDuplicate={onDuplicateGroup}
          onDelete={onDeleteGroup}
          onSetAsDefault={onSetDefaultGroup}
          renameDisabledReason={disabledReasons.rename}
          deleteDisabledReason={disabledReasons.delete}
          setAsDefaultDisabledReason={disabledReasons.setAsDefault}
        />
      ),
      cell: ({ row }) => (
        <PermissionCheckboxCell
          rowIndex={row.index}
          policy={row.original.policy}
          groupKey={groupKey}
          policyIndexMap={policyIndexMap}
          t={t}
          alwaysDisabled={isSuperAdmin}
        />
      ),
    };
  });

  return [policyColumn, ...groupColumns];
};
